import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TransactionSummary } from '../../models/transactions/transactionSummary';
import { Merchant } from '../../models/merchant/merchant';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';
import { StorageService } from '../../core/storage/storageService';
import { TransactionService } from '../../models/transactions/transactionService';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { compact, compactCount } from '../../../../../../libs/Shared/components/graphs/dual-axis-bar-chart.component';


type GraphPeriod = 'trailing' | 'day' | 'week' | 'month';

@Component({
  selector: 'app-merchant-transaction-graphs',
  templateUrl: './merchant-transaction-graphs.component.html',
  standalone: false,
})
export class MerchantTransactionGraphsComponent {
  /** Overrides the merchant picked up from storage. Omit for all merchants. */
  @Input() merchant: Merchant | undefined;

  rows: TransactionSummary[] = [];
  loading = false;

  period: GraphPeriod = 'trailing';

  // Current selections, as strings because app-selectbox emits ids.
  selectedPeriod = 'trailing';
  selectedDays = '30';
  selectedYear = String(new Date().getFullYear());
  selectedMonth = String(new Date().getMonth() + 1);
  selectedWeekSpan = '12';
  selectedMonthSpan = '12';
  selectedMaxList = '10';

  periodOptions: ListItem[] = [
    new ListItem({ id: 'trailing', name: 'Trailing days' }),
    new ListItem({ id: 'day', name: 'Daily, one month' }),
    new ListItem({ id: 'week', name: 'Weekly' }),
    new ListItem({ id: 'month', name: 'Monthly' }),
  ];

  dayOptions: ListItem[] = [15, 30, 45, 60, 75, 90].map(
    d => new ListItem({ id: String(d), name: `Last ${d} days` }),
  );

  yearOptions: ListItem[] = [];
  monthOptions: ListItem[] = [];

  weekSpanOptions: ListItem[] = [
    new ListItem({ id: '4', name: 'Last 4 weeks' }),
    new ListItem({ id: '8', name: 'Last 8 weeks' }),
    new ListItem({ id: '12', name: 'Last 12 weeks' }),
    new ListItem({ id: '26', name: 'Last 26 weeks' }),
    new ListItem({ id: '52', name: 'Last 52 weeks' }),
  ];

  monthSpanOptions: ListItem[] = [
    new ListItem({ id: '3', name: 'Last 3 months' }),
    new ListItem({ id: '6', name: 'Last 6 months' }),
    new ListItem({ id: '12', name: 'Last 12 months' }),
    new ListItem({ id: '24', name: 'Last 24 months' }),
    new ListItem({ id: '36', name: 'Last 36 months' }),
  ];

  maxListOptions: ListItem[] = [5, 10, 15, 20, 25, 30].map(
    n => new ListItem({ id: String(n), name: `${n} or fewer` }),
  );

  // Passed to the chart as [format1] / [format2].
  currency = (n: number) => (n < 0 ? '-$' : '$') + compact(Math.abs(n));
  count = compactCount;

  private static readonly MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  constructor(
    private router: Router,
    private storage: StorageService,
    private transactionService: TransactionService,
    private uiErrorHandler: UIErrorHandler,
  ) {
    const thisYear = new Date().getFullYear();
    for (let y = thisYear; y > thisYear - 5; y--) {
      this.yearOptions.push(new ListItem({ id: String(y), name: String(y) }));
    }
    MerchantTransactionGraphsComponent.MONTH_NAMES.forEach((name, i) => {
      this.monthOptions.push(new ListItem({ id: String(i + 1), name }));
    });
  }

  ngOnInit() {
    // Merchant-scoped routes read the selected location; elsewhere we chart
    // every merchant the user is allowed to see.
    if (!this.merchant && this.router.url.includes('/merchant')) {
      this.merchant = this.storage.getSelectedLocation();
    }
    this.load();
  }

  // --- selection handlers --------------------------------------------------

  handlePeriodChange(value: string) {
    this.selectedPeriod = value;
    this.period = value as GraphPeriod;
    this.load();
  }

  handleDaysChange(value: string) {
    this.selectedDays = value;
    this.load();
  }

  handleYearChange(value: string) {
    this.selectedYear = value;
    this.load();
  }

  handleMonthChange(value: string) {
    this.selectedMonth = value;
    this.load();
  }

  handleWeekSpanChange(value: string) {
    this.selectedWeekSpan = value;
    this.load();
  }

  handleMonthSpanChange(value: string) {
    this.selectedMonthSpan = value;
    this.load();
  }

  handleMaxListChange(value: string) {
    this.selectedMaxList = value;
    this.load();
  }

  // --- loading -------------------------------------------------------------

  load() {
    const maxList = Number(this.selectedMaxList) || 10;
    this.loading = true;

    let request: Promise<TransactionSummary[]>;

    switch (this.period) {
      case 'day': {
        request = this.transactionService.getDailyGraph(
          Number(this.selectedYear),
          Number(this.selectedMonth),
          this.merchant,
          maxList,
        );
        break;
      }

      case 'week': {
        const span = Number(this.selectedWeekSpan) || 12;
        const today = new Date();
        const first = new Date(today);
        first.setDate(first.getDate() - (span - 1) * 7);

        const start = this.isoWeek(first);
        const end = this.isoWeek(today);

        request = this.transactionService.getWeeklyGraph(
          start.year, start.week, end.year, end.week, this.merchant, maxList,
        );
        break;
      }

      case 'month': {
        const span = Number(this.selectedMonthSpan) || 12;
        const today = new Date();
        const first = new Date(today.getFullYear(), today.getMonth() - (span - 1), 1);

        request = this.transactionService.getMonthlyGraph(
          first.getFullYear(), first.getMonth() + 1,
          today.getFullYear(), today.getMonth() + 1,
          this.merchant, maxList,
        );
        break;
      }

      case 'trailing':
      default: {
        request = this.transactionService.getTrailingDailyGraph(
          this.toDateString(new Date()),
          Number(this.selectedDays) || 30,
          this.merchant,
          maxList,
        );
        break;
      }
    }

    request
      .then(rows => {
        this.rows = rows ?? [];
      })
      .catch(error => {
        this.uiErrorHandler.handleError(error);
      })
      .then(() => {
        this.loading = false;
      });
  }

  // --- display -------------------------------------------------------------

  /** Buckets wider than a day need the date on each listed transaction. */
  get fullDates(): boolean {
    return this.period === 'week' || this.period === 'month';
  }

  get chartRatio(): number {
    return this.period === 'month' ? 3.2 : 2.6;
  }

  get rangeLabel(): string {
    switch (this.period) {
      case 'day':
        return `${MerchantTransactionGraphsComponent.MONTH_NAMES[Number(this.selectedMonth) - 1]} ${this.selectedYear}`;
      case 'week':
        return `Last ${this.selectedWeekSpan} weeks`;
      case 'month':
        return `Last ${this.selectedMonthSpan} months`;
      default:
        return `Last ${this.selectedDays} days`;
    }
  }

  // --- helpers -------------------------------------------------------------

  /** ISO week-year and week number, matching Postgres isoyear/week. */
  private isoWeek(date: Date): { year: number; week: number } {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;            // Monday = 1 ... Sunday = 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);    // shift to the week's Thursday
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return { year: d.getUTCFullYear(), week };
  }

  private toDateString(date: Date): string {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${m}-${d}`;
  }
}